/*
  Cópia de `plataforma-web/src/dados/sessao-no-cookie.ts`.

  É cópia, e não importação, por causa do Docker: o contexto de build deste
  produto é `memoire/`, e um `../../plataforma-web/...` simplesmente não existe
  dentro da imagem. Trocar o contexto para a raiz faria os três produtos
  reconstruírem quando qualquer um mudasse.

  **Tudo abaixo do marcador é igual byte a byte ao original.** O `implantar.sh`
  confere antes de subir e recusa se divergirem — duas cópias que se afastam em
  silêncio foi o que motivou o aviso no `CLAUDE.md` deste repositório.

  Para mudar: mude o original e rode `node memoire/scripts/copia-a-sessao.mjs`.
*/

/* ——— cópia verbatim a partir daqui ——— */
/*
  Onde a sessão do Supabase fica guardada.

  O padrão da biblioteca é `localStorage`, que é **preso à origem**. Com os três
  produtos em subdomínios — `frances.`, `memoire.`, `etudie.` — entrar num não
  entraria nos outros: mesma pessoa, mesmo navegador, três sessões.

  Aqui ela vai para um cookie com escopo `.uninativ.com`, que os três leem.

  ---

  **O custo, dito com clareza:** um XSS em qualquer um dos três passa a alcançar
  a sessão dos três. Com `localStorage` o estrago ficava num. Não é motivo para
  não fazer — é a mesma exposição que qualquer login único tem — mas é motivo
  para o `Content-Security-Policy` dos três sair da fila de depois.

  **Não dá para usar `HttpOnly`.** É a biblioteca no navegador que lê e renova o
  token; um cookie que o JavaScript não enxerga seria um cookie que ela não usa.
  Fechar isso de verdade exigiria a sessão virar responsabilidade do servidor, e
  aí os três precisariam de servidor de sessão — outro projeto.
*/

/*
  O domínio compartilhado.

  Explícito, e não derivado do `hostname`: derivar exigiria adivinhar onde
  termina o domínio registrável, e `co.uk` e parentes fazem essa adivinhação
  errar. Uma linha no `.env` é mais barata que uma heurística.
*/
const DOMINIO = import.meta.env.VITE_DOMINIO_SESSAO || 'uninativ.com'

/*
  4096 bytes é o teto de um cookie, e o limite tem de valer para a linha
  inteira — nome, valor e atributos. 3200 deixa folga confortável para
  `Domain`, `Path`, `Secure`, `SameSite` e o nome da chave.

  Medido em 20/09: a sessão inteira dá 2832 bytes depois de codificada, então
  hoje cabe num pedaço só. Partir mesmo assim é o que evita a falha de amanhã:
  o `access_token` é um JWT, e basta alguém acrescentar uma claim para ele
  passar do teto. Estourar silenciosamente apareceria como "deslogou sozinho",
  que é o defeito mais caro de diagnosticar que existe.
*/
const PEDACO = 3200

/** Quantos pedaços procurar antes de desistir. Nove são 28 KB — ninguém chega lá. */
const MAX_PEDACOS = 9

const ehSeguro = (): boolean =>
  typeof location !== 'undefined' && location.protocol === 'https:'

/**
 * O `Domain` só entra quando o endereço atual pertence a ele.
 *
 * Em `localhost` e no endereço da tailnet não há domínio compartilhado: um
 * navegador **recusa** um cookie cujo `Domain` não case com o host, e o
 * resultado seria a sessão não gravar em lugar nenhum durante o
 * desenvolvimento. Sem o atributo, o cookie vale para o host atual, que é
 * exatamente o que se quer lá.
 */
function atributoDeDominio(): string {
  if (typeof location === 'undefined') return ''
  const host = location.hostname
  if (host === DOMINIO || host.endsWith('.' + DOMINIO)) return `; Domain=.${DOMINIO}`
  return ''
}

function lerCru(nome: string): string | null {
  if (typeof document === 'undefined') return null
  const alvo = nome + '='
  for (const parte of document.cookie.split('; ')) {
    if (parte.startsWith(alvo)) return decodeURIComponent(parte.slice(alvo.length))
  }
  return null
}

function gravarCru(nome: string, valor: string): void {
  if (typeof document === 'undefined') return
  document.cookie =
    `${nome}=${encodeURIComponent(valor)}` +
    atributoDeDominio() +
    '; Path=/' +
    /* Um ano. Quem manda na validade é o token, não o cookie: o refresh renova
       a sessão sozinho, e um cookie curto derrubaria quem voltasse depois. */
    '; Max-Age=31536000' +
    /* `Lax` e não `Strict`: com `Strict`, chegar por um link de fora — do
       e-mail do código, por exemplo — traria a pessoa deslogada. */
    '; SameSite=Lax' +
    (ehSeguro() ? '; Secure' : '')
}

function apagarCru(nome: string): void {
  if (typeof document === 'undefined') return
  document.cookie =
    `${nome}=` + atributoDeDominio() + '; Path=/; Max-Age=0; SameSite=Lax' +
    (ehSeguro() ? '; Secure' : '')
}

/**
 * Guarda a sessão num cookie de domínio, partida quando não couber.
 *
 * A interface é a que o `supabase-js` espera: `getItem`, `setItem`,
 * `removeItem`. Ele não sabe que virou cookie.
 */
export const sessaoNoCookie = {
  getItem(chave: string): string | null {
    const inteiro = lerCru(chave)
    if (inteiro !== null) return inteiro

    /* Partida: os pedaços vêm em `chave.0`, `chave.1`… e param no primeiro
       ausente. Parar no primeiro buraco, e não seguir procurando, é o que
       impede montar uma sessão pela metade — que passaria pela biblioteca como
       token corrompido, sem dizer por quê. */
    const pedacos: string[] = []
    for (let i = 0; i < MAX_PEDACOS; i++) {
      const p = lerCru(`${chave}.${i}`)
      if (p === null) break
      pedacos.push(p)
    }
    if (pedacos.length) return pedacos.join('')

    /*
      Último recurso: o `localStorage`.

      Serve a duas coisas. Uma é a migração — quem já estava logado antes desta
      mudança continua logado, em vez de ser deslogado por uma troca de
      mecanismo que não é problema dele. A outra é a suíte, que monta uma sessão
      escrevendo direto ali.

      O cookie vem primeiro de propósito: quando os dois existem, quem manda é
      o novo.
    */
    try {
      return localStorage.getItem(chave)
    } catch {
      return null
    }
  },

  setItem(chave: string, valor: string): void {
    /* Antes de gravar, some com o que houver na outra forma — senão uma sessão
       velha no `localStorage` voltaria a valer no dia em que o cookie
       expirasse, e a pessoa entraria com uma identidade de semanas atrás. */
    try {
      localStorage.removeItem(chave)
    } catch {
      /* navegador sem armazenamento: nada a limpar */
    }

    if (valor.length <= PEDACO) {
      gravarCru(chave, valor)
      limparPedacos(chave, 0)
      return
    }

    apagarCru(chave)
    let i = 0
    for (let pos = 0; pos < valor.length; pos += PEDACO, i++) {
      gravarCru(`${chave}.${i}`, valor.slice(pos, pos + PEDACO))
    }
    /* Uma sessão menor que a anterior deixaria pedaços órfãos, e a leitura os
       concatenaria no fim do JSON. */
    limparPedacos(chave, i)
  },

  removeItem(chave: string): void {
    apagarCru(chave)
    limparPedacos(chave, 0)
    try {
      localStorage.removeItem(chave)
    } catch {
      /* idem */
    }
  },
}

function limparPedacos(chave: string, apartirDe: number): void {
  for (let i = apartirDe; i < MAX_PEDACOS; i++) apagarCru(`${chave}.${i}`)
}
